import { useEffect, useMemo, useState } from "react";
import type { SortDirection } from "../components/TableControls.js";

type SortValue = string | number | boolean | undefined;

export function useClientTable<T>(items: T[], searchText: (item: T) => string,
  sorters: Record<string, (item: T) => SortValue>, initialSort: string, initialDirection: SortDirection = "desc") {
  const [query, setQueryState] = useState("");
  const [sort, setSort] = useState(initialSort);
  const [direction, setDirection] = useState<SortDirection>(initialDirection);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(25);
  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const matches = normalized ? items.filter((item) => searchText(item).toLowerCase().includes(normalized)) : [...items];
    const accessor = sorters[sort];
    if (!accessor) return matches;
    return matches.sort((left, right) => compare(accessor(left), accessor(right)) * (direction === "asc" ? 1 : -1));
  }, [items, query, sort, direction, searchText, sorters]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  useEffect(() => { if (page > pageCount) setPage(pageCount); }, [page, pageCount]);
  return {
    items: filtered.slice((page - 1) * pageSize, page * pageSize), total: filtered.length,
    query, setQuery: (value: string) => { setQueryState(value); setPage(1); }, sort, setSort,
    direction, setDirection, page, setPage, pageSize,
    setPageSize: (value: number) => { setPageSizeState(value); setPage(1); },
  };
}

function compare(left: SortValue, right: SortValue): number {
  if (left == null) return right == null ? 0 : -1;
  if (right == null) return 1;
  if (typeof left === "number" && typeof right === "number") return left - right;
  if (typeof left === "boolean" && typeof right === "boolean") return Number(left) - Number(right);
  return String(left).localeCompare(String(right), undefined, {numeric: true, sensitivity: "base"});
}
